// react
import { MouseEvent, useState } from 'react';

// types
import { ErrorCause, Feedback, Invoice, InvoiceStatus } from '../../../@types';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, Grid, Typography } from '@mui/material';

// ui
import { AlertFeedback, AlertFeedbackProps } from '../../ui/AlertFeedback/AlertFeedback';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// utils
import { translateError } from '../../../utils/translations';

// props
export interface DialogUpdateInvoiceStatusProps {
	invoice: Invoice;
	isOpen: boolean;
	status: Extract<InvoiceStatus, 'PENDING_REVIEW' | 'READY_FOR_PAYMENT' | 'REJECTED'>;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
}

export const DialogUpdateInvoiceStatus = ({
	invoice,
	isOpen,
	status,
	onClose,
	onSuccess,
}: DialogUpdateInvoiceStatusProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// state
	const [feedback, setFeedback] = useState<AlertFeedbackProps | null>(null);

	const dialogByStatus: Record<
		DialogUpdateInvoiceStatusProps['status'],
		{
			alert: string;
			contentKey: string;
			severity: 'error' | 'success' | 'warning';
			submitLabel: string;
			title: string;
		}
	> = {
		PENDING_REVIEW: {
			alert: t('dialogUpdateInvoiceStatus.pending.alert'),
			contentKey: 'dialogUpdateInvoiceStatus.pending.content',
			severity: 'warning',
			submitLabel: t('resetToPendingReview'),
			title: t('resetToPendingReview'),
		},
		READY_FOR_PAYMENT: {
			alert: t('dialogUpdateInvoiceStatus.approved.alert'),
			contentKey: 'dialogUpdateInvoiceStatus.approved.content',
			severity: 'success',
			submitLabel: t('approveInvoice'),
			title: t('approveInvoice'),
		},
		REJECTED: {
			alert: t('dialogUpdateInvoiceStatus.rejected.alert'),
			contentKey: 'dialogUpdateInvoiceStatus.rejected.content',
			severity: 'error',
			submitLabel: t('rejectInvoice'),
			title: t('rejectInvoice'),
		},
	};

	const dialog = dialogByStatus[status];

	const { fetchRequest: updateInvoiceStatus, isLoading: isLoadingUpdateInvoiceStatus } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_V2_URL}/invoices/${invoice.id}`,
		options: {
			method: 'PATCH',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				'Content-Type': 'application/json',
				User: String(idToken),
			},
			body: JSON.stringify({ status }),
		},
		onError: (cause) => {
			const feedbackNew: AlertFeedbackProps & { items: Feedback[] } = {
				items: [],
			};

			feedbackNew.items.push({
				alert: {
					children: translateError({ cause: cause as ErrorCause, t }),
					severity: 'error',
				},
			});
			setFeedback(feedbackNew);
		},
		onSuccess: () => {
			if (onSuccess) {
				onSuccess();
			}

			if (onClose) {
				onClose();
			}
		},
	});

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		setFeedback(null);
		onClose(e);
	};

	const handleSubmit = () => {
		setFeedback(null);
		updateInvoiceStatus();
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					children: dialog.submitLabel,
					color:
						status === 'REJECTED' ? 'error' : status === 'PENDING_REVIEW' ? 'warning' : 'primary',
					loading: isLoadingUpdateInvoiceStatus,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{
				type: 'invoice',
			}}
			isOpen={isOpen}
			maxWidth={360}
			onClose={handleClose}
			subtitle={{
				children: invoice.num,
			}}
			title={{
				children: dialog.title,
			}}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Trans
						components={{
							strong: <Typography color="brand.blue.600" component="span" fontWeight={600} />,
						}}
						i18nKey={dialog.contentKey}
						values={{ num: invoice.num }}
					/>
				</Grid>
				<Grid item xs={12}>
					<Alert severity={dialog.severity}>{dialog.alert}</Alert>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<AlertFeedback {...feedback} />
					</Grid>
				)}
			</Grid>
		</DialogActions>
	);
};
